import React from 'react';
import FileUploadSection from './FileUploadSection';

const PayableForm = ({
  mode,
  paymentData,
  carrierInfo,
  loadInfo,
  register,
  errors,
  watch,
  setValue,
  handleSubmit,
  images,
  pdfs,
  existingImages,
  existingPdfs,
  handleImageChange,
  handlePdfChange,
  removeImage,
  removePdf,
  removeExistingImage,
  removeExistingPdf,
  payableStatuses,
  paymentMethods,
  calculateNetAmount,
  error,
  isSubmitting,
  onCancel,
  onSave
}) => {
  const isEditMode = mode === 'edit';
  const formValues = watch();

  return (
    <form onSubmit={handleSubmit(onSave)} className="payment-wizard__payment-form-expanded">
      <div className="payment-wizard__form-section">
        <h4 className="payment-wizard__form-title">Payment Payable</h4>
        
        {/* Carrier Info - Read-only */}
        {carrierInfo && (
          <div className="payment-wizard__load-info payment-wizard__info-readonly">
            <h5>Carrier Information</h5>
            <p><strong>Company Name:</strong> {carrierInfo.companyName || carrierInfo.name || '--'}</p>
            <p><strong>Email:</strong> {carrierInfo.email || '--'}</p>
            <p><strong>Phone:</strong> {carrierInfo.phone || carrierInfo.phoneNumber || '--'}</p>
            {carrierInfo.mcNumber && <p><strong>MC Number:</strong> {carrierInfo.mcNumber}</p>}
            {carrierInfo.dotNumber && <p><strong>DOT Number:</strong> {carrierInfo.dotNumber}</p>}
            {carrierInfo.address && (
              <p><strong>Address:</strong> {typeof carrierInfo.address === 'object' 
                ? `${carrierInfo.address.address || ''}, ${carrierInfo.address.city || ''}, ${carrierInfo.address.state || ''} ${carrierInfo.address.zipCode || ''}`.trim()
                : carrierInfo.address}</p>
            )}
          </div>
        )}

        {loadInfo && (
          <div className="payment-wizard__load-info payment-wizard__info-readonly">
            <h5>Load Information</h5>
            <p><strong>Order ID:</strong> {loadInfo.orderId || loadInfo._id || '--'}</p>
            <p><strong>Carrier Rate:</strong> ${loadInfo.carrierRate || paymentData?.carrierRate || '0'}</p>
            <p><strong>Status:</strong> {loadInfo.status || '--'}</p>
          </div>
        )}

        {/* Read-only fields in edit mode */}
        {isEditMode && (
          <>
            <div className="payment-wizard__form-field">
              <label className="payment-wizard__form-label">Amount</label>
              <input
                type="text"
                value={`$${paymentData?.amount || paymentData?.grossAmount || '0'}`}
                className="form-control"
                disabled
                readOnly
              />
            </div>
            <div className="payment-wizard__form-row">
              <div className="payment-wizard__form-field">
                <label className="payment-wizard__form-label">Scheduled Date</label>
                <input
                  type="text"
                  value={paymentData?.scheduledDate ? new Date(paymentData.scheduledDate).toLocaleDateString() : '--'}
                  className="form-control"
                  disabled
                  readOnly
                />
              </div>
              <div className="payment-wizard__form-field">
                <label className="payment-wizard__form-label">Payment Method</label>
                <input
                  type="text"
                  value={paymentData?.paymentMethod || '--'}
                  className="form-control"
                  disabled
                  readOnly
                />
              </div>
            </div>
            {paymentData?.deductions && (
              <div className="payment-wizard__deductions-section">
                <h5 className="payment-wizard__section-subtitle">Deductions</h5>
                <div className="payment-wizard__form-row">
                  <div className="payment-wizard__form-field">
                    <label className="payment-wizard__form-label">Quick Pay Fee</label>
                    <input type="text" value={`$${paymentData.deductions.quickPayFee || '0'}`} className="form-control" disabled readOnly />
                  </div>
                  <div className="payment-wizard__form-field">
                    <label className="payment-wizard__form-label">Factoring Fee</label>
                    <input type="text" value={`$${paymentData.deductions.factoringFee || '0'}`} className="form-control" disabled readOnly />
                  </div>
                </div>
                <div className="payment-wizard__form-row">
                  <div className="payment-wizard__form-field">
                    <label className="payment-wizard__form-label">Other Deductions</label>
                    <input type="text" value={`$${paymentData.deductions.otherDeductions || '0'}`} className="form-control" disabled readOnly />
                  </div>
                  <div className="payment-wizard__form-field">
                    <label className="payment-wizard__form-label">Net Amount</label>
                    <input type="text" value={`$${paymentData.netAmount || '0'}`} className="form-control" disabled readOnly />
                  </div>
                </div>
                {paymentData.deductions.deductionNotes && (
                  <div className="payment-wizard__form-field">
                    <label className="payment-wizard__form-label">Deduction Notes</label>
                    <input type="text" value={paymentData.deductions.deductionNotes} className="form-control" disabled readOnly />
                  </div>
                )}
              </div>
            )}
            <div className="payment-wizard__form-field">
              <label className="payment-wizard__form-label">Notes</label>
              <textarea
                value={paymentData?.notes || ''}
                className="form-control"
                rows={3}
                disabled
                readOnly
              />
            </div>
          </>
        )}

        {/* Editable fields in add mode */}
        {!isEditMode && (
          <>
            <div className="payment-wizard__form-field">
              <label className="payment-wizard__form-label">Amount *</label>
              <input
                type="number"
                {...register("amount", {
                  required: "Amount is required",
                  min: { value: 0.01, message: "Amount must be greater than 0" }
                })}
                className={`form-control ${errors.amount ? 'input-error' : ''}`}
                placeholder="Enter amount"
              />
              {errors.amount && (
                <span className="field-error">{errors.amount.message}</span>
              )}
            </div>
            <div className="payment-wizard__form-row">
              <div className="payment-wizard__form-field">
                <label className="payment-wizard__form-label">Scheduled Date</label>
                <input
                  type="date"
                  {...register("scheduledDate")}
                  className="form-control"
                />
              </div>
              <div className="payment-wizard__form-field">
                <label className="payment-wizard__form-label">Payment Method</label>
                <select
                  {...register("paymentMethod")}
                  className="form-select"
                >
                  <option value="">Select method</option>
                  {paymentMethods.map(method => (
                    <option key={method} value={method}>{method}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="payment-wizard__deductions-section">
              <h5 className="payment-wizard__section-subtitle">Deductions</h5>
              <div className="payment-wizard__form-row">
                <div className="payment-wizard__form-field">
                  <label className="payment-wizard__form-label">Quick Pay Fee</label>
                  <input
                    type="number"
                    {...register("quickPayFee")}
                    className="form-control"
                    placeholder="0.00"
                  />
                </div>
                <div className="payment-wizard__form-field">
                  <label className="payment-wizard__form-label">Factoring Fee</label>
                  <input
                    type="number"
                    {...register("factoringFee")}
                    className="form-control"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="payment-wizard__form-row">
                <div className="payment-wizard__form-field">
                  <label className="payment-wizard__form-label">Other Deductions</label>
                  <input
                    type="number"
                    {...register("otherDeductions")}
                    className="form-control"
                    placeholder="0.00"
                  />
                </div>
                <div className="payment-wizard__form-field">
                  <label className="payment-wizard__form-label">Net Amount</label>
                  <input
                    type="text"
                    value={`$${calculateNetAmount()}`}
                    className="form-control"
                    disabled
                  />
                </div>
              </div>
              <div className="payment-wizard__form-field">
                <label className="payment-wizard__form-label">Deduction Notes</label>
                <input
                  type="text"
                  {...register("deductionNotes")}
                  className="form-control"
                  placeholder="Reason for deductions..."
                />
              </div>
            </div>
            <div className="payment-wizard__form-field">
              <label className="payment-wizard__form-label">Notes</label>
              <textarea
                {...register("notes")}
                className="form-control"
                rows={3}
                placeholder="Enter notes..."
              />
            </div>
          </>
        )}

        {/* Editable fields - Always editable (in both add and edit mode) */}
        <div className="payment-wizard__form-row">
          <div className="payment-wizard__form-field">
            <label className="payment-wizard__form-label">Account Number</label>
            <input
              type="text"
              {...register("accountNumber")}
              className="form-control"
              placeholder="Enter account number"
            />
          </div>
          <div className="payment-wizard__form-field">
            <label className="payment-wizard__form-label">Routing Number</label>
            <input
              type="text"
              {...register("routing")}
              className="form-control"
              placeholder="Enter routing number"
            />
          </div>
        </div>
        <div className="payment-wizard__form-field">
          <label className="payment-wizard__form-label">Bank Name</label>
          <input
            type="text"
            {...register("bank")}
            className="form-control"
            placeholder="Enter bank name"
          />
        </div>

        {/* File Uploads */}
        <FileUploadSection
          title="Images"
          accept="image/*"
          files={images}
          existingFiles={existingImages}
          handleChange={handleImageChange}
          removeFile={removeImage}
          removeExisting={removeExistingImage}
        />

        <FileUploadSection
          title="PDFs"
          accept=".pdf"
          files={pdfs}
          existingFiles={existingPdfs}
          handleChange={handlePdfChange}
          removeFile={removePdf}
          removeExisting={removeExistingPdf}
        />
      </div>

      {error && <div className="payment-wizard__error">{error}</div>}

      <div className="payment-wizard__form-actions">
        <button
          className="payment-wizard__cancel-btn btn"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button 
          type="submit"
          className="payment-wizard__save-btn btn" 
          disabled={isSubmitting || (!isEditMode && !formValues.amount)}
        >
          {isSubmitting ? 'Saving...' : (mode === 'edit' ? 'Update' : 'Save')}
        </button>
      </div>
    </form>
  );
};

export default PayableForm;

